import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import ReactPlayer from "react-player/lazy";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import "./Exercise.css";

import Button from "../App/Common/Button/Button";
import MultiChoice from "../Test/TestOptions/MultiChoice/MultiChoice";
import SingleChoice from "../Test/TestOptions/SingleChoice/SingleChoice";
import HorizontalTree from "../App/Common/HorizontalTree/HorizontalTree";
import FullScreenImage from "../App/Common/FullScreenImage/FullScreenImage";
import QuestionFilterCheckbox from "../App/Common/Inputs/Checkboxes/QuestionFilterCheckbox/QuestionFilterCheckbox";
import ShortAnswer from "../Test/TestOptions/ShortAnswer/ShortAnswer";
import Essay from "../Test/TestOptions/Essay/Essay";
import File from "../Test/TestOptions/File/File";
import FillGaps from "../Test/TestOptions/FillGaps/FillGaps";
import Sequence from "../Test/TestOptions/DnD questions/Sequence/Sequence";
import Pairs from "../Test/TestOptions/DnD questions/Pairs/Pairs";

import MultiResult from "../TestResults/ResultOptions/Multi/MultiResult";
import SingleResult from "../TestResults/ResultOptions/Single/SingleResult";
import ShortAnswerResult from "../TestResults/ResultOptions/ShortAnswer/ShortAnswerResult";
import EssayResult from "../TestResults/ResultOptions/Essay/EssayResult";
import FileResult from "../TestResults/ResultOptions/File/FileResult";
import FillGapsResult from "../TestResults/ResultOptions/FillGaps/FillGapsResult";
import SequenceResult from "../TestResults/ResultOptions/Sequence/SequenceResult";
import PairsResult from "../TestResults/ResultOptions/Pairs/PairsResult";
import Popup from "../App/Common/Popup/Popup";

import api from "../../Utils/Api";
import { CurrentUserContext } from "../../Contexts/CurrentUserContext";
import { JourneyContext } from "../../Contexts/JourneyContext";
import Comment from "../Test/Comments/Comment";

const Exercise = () => {
  const { token, user } = useContext(CurrentUserContext);
  const { currentPlace, breadCrumbs, setBreadCrumbs, setCurrentPlace } =
    useContext(JourneyContext);

  let params = useParams();
  let selectedTestId = parseInt(params.id);
  let navigate = useNavigate();

  const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
  const [leftColumnSize, setLeftColumnSize] = useState(undefined);
  const [questions, setQuestions] = useState(undefined);
  const [currentQuestion, setCurrentQuestion] = useState(undefined);
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState({});
  const [answers, setAnswers] = useState([]);
  const [isFilterChecked, setIsFilterChecked] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [time, setTime] = useState(0);
  const [attemptId, setAttemptId] = useState(undefined);
  const [parentStep, setParentStep] = useState(undefined);
  const [isPhotoOpened, setIsPhotoOpened] = useState(false);
  const [autoSystemReport, setAutoSystemReport] = useState({});
  const [showPopUp, setShowPopup] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);
  function handlePopUp() {
    setShowPopup(!showPopUp);
  }

  const shuffleQuestions = (questions) => {
    for (let i = questions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [questions[i], questions[j]] = [questions[j], questions[i]];
    }
    return questions;
  };

  useEffect(() => {
    api
      .getTest(token, selectedTestId)
      .then((test) => {
        const shuffledQuestions = shuffleQuestions(test.questions);
        const orderedQuestions = shuffledQuestions.map((element, index) => {
          element.leftColumnNumber = index;
          return element;
        });
        setQuestions(orderedQuestions);
        setCurrentQuestion(orderedQuestions[0]);
        setParentStep(test.step_id);
        setUnansweredQuestions(orderedQuestions);
        setBreadCrumbs(test.parents);
        setCurrentPlace(test.parents.slice(-1)[0]);
        setAutoSystemReport(test.auto_system_report);

        api
          .startTest(token, selectedTestId)
          .then((responce) => {
            setAttemptId(responce.test_result_id);
          })
          .catch((e) => {
            console.log(e);
          });
      })
      .catch((e) => {
        console.log(e);
        navigate(`/locked_resource`, { replace: true });
      });
    return () => {
      setBreadCrumbs([]);
    };
  }, []);

  useEffect(() => {
    if (questions) {
      let filteredQuestions = questions?.filter(
        (question) =>
          !Object.keys(currentAnswers).includes(question.id.toString())
      );
      setUnansweredQuestions(filteredQuestions);
      setCurrentQuestionNumber(0);
      setCurrentQuestion(isFilterChecked ? filteredQuestions[0] : questions[0]);
    }
  }, [isFilterChecked]);

  useEffect(() => {
    if (questions?.length < 13) {
      setLeftColumnSize("thin");
    } else if (questions?.length < 22) {
      setLeftColumnSize("medium");
    } else if (questions?.length >= 22) {
      setLeftColumnSize("wide");
    }
  }, [questions]);

  const setAnswer = () => {
    setIsAnswered(true);
    if (
      answers === null ||
      answers === undefined ||
      answers.length === 0 ||
      (answers.filenames &&
        answers.filenames.length === 0 &&
        answers.third_party_url === "") ||
      (Array.isArray(answers) &&
        answers.every(
          (answer) =>
            answer && answer.right_ids && answer.right_ids.length === 0
        )) ||
      (Array.isArray(answers) && answers.some((a) => a === undefined))
    ) {
      handlePopUp();
      return;
    }

    switch (currentQuestion.type) {
      case "single":
      case "multiple":
      case "sequence":
        api
          .checkExerciseSingleMultiSequence(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers
          )
          .then((res) => {
            let newAnswers = { ...currentAnswers };
            newAnswers[res.id] = res.answers;
            setCurrentAnswers(newAnswers);
            setCurrentAnswer(res.answers);
            setCurrentQuestion({
              ...res,
              leftColumnNumber: currentQuestionNumber,
            });
            questions[currentQuestionNumber] = {
              ...res,
              leftColumnNumber: currentQuestionNumber,
            };
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      case "file":
        api
          .checkExerciseFile(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers.files,
            answers.filenames,
            answers.third_party_url
          )
          .then((res) => {
            if (
              res.answers.file_address !== null ||
              res.answers.third_party_url !== ""
            ) {
              let newAnswers = { ...currentAnswers };
              newAnswers[res.id] = res;
              setCurrentAnswers(newAnswers);
              setCurrentAnswer(res);
              setCurrentQuestion({
                ...res,
                leftColumnNumber: currentQuestionNumber,
              });
              questions[currentQuestionNumber] = {
                ...res,
                leftColumnNumber: currentQuestionNumber,
              };
            }
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      case "essay":
        api
          .checkExerciseEssay(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers
          )
          .then((res) => {
            let newAnswers = { ...currentAnswers };
            newAnswers[res.id] = res.answers;
            setCurrentAnswers(newAnswers);
            setCurrentAnswer(res.answers);
            setCurrentQuestion({
              ...res,
              leftColumnNumber: currentQuestionNumber,
            });
            questions[currentQuestionNumber] = {
              ...res,
              leftColumnNumber: currentQuestionNumber,
            };
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      case "blanks":
        api
          .checkExerciseBlanks(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers
          )
          .then((res) => {
            let newAnswers = { ...currentAnswers };
            newAnswers[res.id] = res.answers;
            setCurrentAnswers(newAnswers);
            setCurrentAnswer(res.answers);
            setCurrentQuestion({
              ...res,
              leftColumnNumber: currentQuestionNumber,
            });
            questions[currentQuestionNumber] = {
              ...res,
              leftColumnNumber: currentQuestionNumber,
            };
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      case "short":
        api
          .checkExerciseShortAnswer(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers
          )
          .then((res) => {
            let newAnswers = { ...currentAnswers };
            newAnswers[res.id] = res.answers;
            setCurrentAnswers(newAnswers);
            setCurrentAnswer(res.answers);
            setCurrentQuestion({
              ...res,
              leftColumnNumber: currentQuestionNumber,
            });
            questions[currentQuestionNumber] = {
              ...res,
              leftColumnNumber: currentQuestionNumber,
            };
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      case "pairs":
        api
          .checkExercisePairs(
            token,
            selectedTestId,
            attemptId,
            currentQuestion.id,
            answers
          )
          .then((res) => {
            if (res.answers.length !== 0) {
              let newAnswers = { ...currentAnswers };
              newAnswers[res.id] = res.answers;
              setCurrentAnswers(newAnswers);
              setCurrentAnswer(res.answers);
              setCurrentQuestion({
                ...res,
                leftColumnNumber: currentQuestionNumber,
              });
              questions[currentQuestionNumber] = {
                ...res,
                leftColumnNumber: currentQuestionNumber,
              };
            }
          })
          .catch((e) => {
            console.log(e);
            handlePopUp();
          });
        break;
      default:
        return console.log("UNEXPECTED QUESTION TYPE");
    }

    setAnswers([]);
  };

  const skipQuestion = () => {
    if (currentQuestionNumber < questions.length - 1) {
      setCurrentQuestionNumber(currentQuestionNumber + 1);
    } else {
      setCurrentQuestionNumber(currentQuestionNumber);
    }
  };

  const returnQuestion = () => {
    if (currentQuestionNumber > 0) {
      setCurrentQuestionNumber(currentQuestionNumber - 1);
    } else {
      setCurrentQuestionNumber(currentQuestionNumber);
    }
  };

  const endTest = () => {
    api
      .endTest(token, selectedTestId, attemptId, time)
      .then((res) => {
        navigate(`/resource/${parentStep}`, { replace: true });
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    if (questions) {
      const currentQuestionObject = questions.find(q => q.leftColumnNumber === currentQuestionNumber);
      if (currentQuestionObject) {
        setCurrentQuestion(currentQuestionObject);
      }
    }
  }, [currentQuestionNumber, questions]);

  useEffect(() => {
    if (questions && currentAnswers[currentQuestion.id]) {
      setCurrentAnswer(currentAnswers[currentQuestion.id]);
    }
    return;
  }, [currentQuestionNumber]);

  const getQuestionType = (type) => {
    switch (type) {
      case "single":
        return (
          <SingleChoice
            options={currentQuestion.answers}
            questionId={currentQuestion.id}
            setAnswers={setAnswers}
            answers={currentAnswers ? currentAnswers[currentQuestion.id] : null}
            allAnswers={currentAnswers}
          />
        );
      case "multiple":
        return (
          <MultiChoice
            options={currentQuestion.answers}
            setAnswers={setAnswers}
            answers={currentAnswers ? currentAnswers[currentQuestion.id] : null}
            questionId={currentQuestion.id}
          />
        );
      case "file":
        return (
          <File
            setAnswers={setAnswers}
            answers={currentAnswers ? currentAnswers[currentQuestion.id] : null}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
            questionId={currentQuestionNumber}
          />
        );
      case "essay":
        return (
          <Essay
            setAnswers={setAnswers}
            questionId={currentQuestion.id}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
          />
        );
      case "blanks":
        return (
          <FillGaps
            setAnswers={setAnswers}
            question={currentQuestion.text}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
          />
        );
      case "sequence":
        return (
          <Sequence
            setAnswers={setAnswers}
            options={currentQuestion.answers}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
          />
        );
      case "short":
        return (
          <ShortAnswer
            setAnswers={setAnswers}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
            questionId={currentQuestion.id}
          />
        );
      case "pairs":
        return (
          <Pairs
            setFinalAnswers={setAnswers}
            options={currentQuestion.answers}
            setCurrentAnswers={setCurrentAnswers}
            currentAnswer={
              currentAnswers ? currentAnswers[currentQuestion.id] : null
            }
          />
        );
      default:
        return "";
    }
  };

  const getAnswerType = (type) => {
    switch (type) {
      case "single":
        return (
          <SingleResult
            options={
              Array.isArray(currentQuestion.answers)
                ? currentQuestion.answers
                : []
            }
          />
        );
      case "multiple":
        return (
          <MultiResult
            options={Array.isArray(currentAnswer) ? currentAnswer : []}
          />
        );
      case "file":
        return <FileResult result={currentQuestion} />;
      case "essay":
        return <EssayResult result={currentQuestion} />;
      case "blanks":
        return <FillGapsResult question={currentQuestion} />;
      case "sequence":
        return (
          <SequenceResult
            answer={
              Array.isArray(currentQuestion.answers)
                ? currentQuestion.answers
                : []
            }
          />
        );
      case "short":
        return <ShortAnswerResult answer={currentQuestion} />;
      case "pairs":
        return (
          <PairsResult
            answer={
              Array.isArray(currentQuestion.answers)
                ? currentQuestion.answers
                : []
            }
          />
        );
      default:
        return "";
    }
  };
  const getExplanationText = (type) => {
    switch (type) {
      case "single":
        return "Выберите один вариант ответа";
      case "multiple":
        return "Выберите один или несколько вариантов ответа";
      case "file":
        return "Прикрепите файл";
      case "essay":
        return "Дайте развернутый ответ на вопрос";
      case "blanks":
        return "Заполните правильно пропуски в тексте";
      case "sequence":
        return "Выберите правильную последовательность";
      case "short":
        return "Дайте короткий ответ на вопрос";
      case "pairs":
        return "Проставьте правильные соответствия";
      default:
        return "";
    }
  };

  const [tab, setTab] = useState(false);
  const [titleTag, setTitle] = useState("_courses");

  const handleOpenTab = (title) => {
    if (title === "Все курсы") {
      setTab(false);
      setTitle("_courses");
    } else if (title === "Календарь") {
      setTab(true);
      setTitle("_calendar");
    } else if (title === "Архив") {
      setTab(true);
      setTitle("_archive");
    }
  };

  const [allAnswered, setAllAnswered] = useState(false);
  const isAllQuestionsAnswered = () => {
    return (
      currentAnswers &&
      questions &&
      Object.keys(currentAnswers).length === questions.length
    );
  };

  useEffect(() => {
    setAllAnswered(isAllQuestionsAnswered());
  }, [currentAnswers, questions]);

  console.log(breadCrumbs);

  const getTestTitle = (breadCrumbs) => {
    const testCrumb = breadCrumbs.find((crumb) => crumb.type === "test");
    const exerciseCrumb = breadCrumbs.find(
      (crumb) => crumb.type === "exercise"
    );

    if (testCrumb) {
      return { typeText: "Тест: ", title: testCrumb.title };
    } else if (exerciseCrumb) {
      return { typeText: "Упражнение: ", title: exerciseCrumb.title };
    } else {
      return "Тест";
    }
  };

  function handlePopUp() {
    setShowPopup(!showPopUp);
  }

  const handleNextQuestionClick = (questionIndex) => {
    const selectedQuestion = questions.find(q => q.leftColumnNumber === questionIndex);
    if (selectedQuestion) {
      setCurrentQuestionNumber(questionIndex);
      setCurrentQuestion(selectedQuestion);
      setIsAnswered(false);
    }
  };

  return (
    questions &&
    currentQuestion && (
      <div className="test__container">
        <div className="openedBlock__container">
          <div className="tab__wrap">
            <div
              className={
                "tab__btn" + (titleTag ? titleTag === "_courses" : "true")
              }
              onClick={() => handleOpenTab("Все курсы")}
            >
              Упражнение
            </div>
          </div>
          <div className="test__body">
            <div className="question__header">
              <div>
                Упражнение:{" "}
                <span className="question__header_title">
                  {getTestTitle(breadCrumbs).title}
                </span>
              </div>
            </div>
            <div className="test__leftColumn">
              <div
                className={`questions__container questions__container_${leftColumnSize}`}
              >
                {questions.map((question) => (
                  <div
                    className={`questions__number questions__number_${leftColumnSize} 
                      ${
                        currentAnswers &&
                        currentAnswers[question.id] &&
                        (question.type !== "essay" || question.type !== "file") &&
                        (question.is_correct > 50
                          ? "questions__number_correct"
                          : question.is_correct > 0
                          ? "questions__number_partlyCorrect"
                          : "questions__number_wrong")
                      } 
                      ${
                        currentAnswers &&
                        currentAnswers[question.id] &&
                        (question.type === "essay" || question.type === "file") &&
                        (!question.result_comment
                          ? "questions__number_grey"
                          : question.is_correct > 50
                          ? "questions__number_correct"
                          : question.is_correct > 0
                          ? "questions__number_partlyCorrect"
                          : "questions__number_wrong")
                      } 
                      ${
                        currentAnswers &&
                        currentAnswers[question.id] &&
                        currentQuestionNumber === question.leftColumnNumber
                          ? "questions__number_currentResult"
                          : ""
                      } 
                      ${
                        currentQuestionNumber === question.leftColumnNumber
                          ? "questions__number_current"
                          : ""
                      }`}
                    key={question.id}
                    onClick={() => handleNextQuestionClick(question.leftColumnNumber)}
                  >
                    {question.leftColumnNumber + 1}
                  </div>
                ))}
              </div>
            </div>
            <div className="question__comment">
              {getExplanationText(currentQuestion.type)}
            </div>
            <DndProvider backend={HTML5Backend}>
              <div>
                {showPopUp && (
                  <Popup onClose={handlePopUp} noButton={true}>
                    <div className="question__popup">
                      Вы не ответили на вопрос.
                      <br />
                      Пожалуйста, дайте ответ перед продолжением.
                    </div>
                  </Popup>
                )}
              </div>
              <div className="test__question">
                <div className="question__container">
                  <div className="question__title">
                    {currentQuestion.leftColumnNumber + 1}.{" "}
                    {currentQuestion.type === "blanks" ? (
                      <p>Заполните правильно пропуски в тексте </p>
                    ) : (
                      parse(currentQuestion.text)
                    )}
                  </div>
                  <div className="question__body">
                    {currentQuestion.attachment_links &&
                      Array.isArray(currentQuestion.attachment_links) && (
                        <div className="question__images">
                          {currentQuestion.attachment_links.map(
                            (pic, index) => (
                              <div
                                className="question__imageContainer"
                                key={index}
                              >
                                <div
                                  className="question__imageIcon"
                                  onClick={() => setIsPhotoOpened(true)}
                                />
                                <img
                                  src={pic}
                                  alt=""
                                  className="question__image"
                                />
                                {isPhotoOpened && (
                                  <FullScreenImage
                                    image={pic}
                                    onClose={setIsPhotoOpened}
                                  />
                                )}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    {currentQuestion.embed_url && (
                      <ReactPlayer
                        className="question__video"
                        url={currentQuestion.embed_url}
                        controls
                        playsinline
                        volume={1}
                        muted={false}
                      />
                    )}
                    {currentAnswers && currentAnswers[currentQuestion.id]
                      ? getAnswerType(currentQuestion.type)
                      : getQuestionType(currentQuestion.type)}
                    {autoSystemReport &&
                      currentAnswers[currentQuestion.id] &&
                      autoSystemReport.is_selected && (
                        <div className="testResult__comment">
                          <span>
                            <span style={{ fontWeight: 700 }}>Результат:</span>{" "}
                            {currentQuestion.is_correct > 50
                              ? autoSystemReport.right_answer
                              : currentQuestion.is_correct > 0
                              ? autoSystemReport.partly_right_answer
                              : autoSystemReport.wrong_answer}
                          </span>
                        </div>
                      )}
                    {currentAnswers[currentQuestion.id] &&
                      currentQuestion.answer_comment && (
                        <div className="testResult__comment">
                          <span>
                            <span style={{ fontWeight: 700 }}>
                              Общий комментарий учителя:
                            </span>{" "}
                            {parse(currentQuestion.answer_comment)}
                          </span>
                        </div>
                      )}
                  </div>
                  <div className="question__buttons questionExercise__buttons">
                    {allAnswered ? (
                      <>
                        <Button
                          buttonText="Завершить упражнение"
                          onClickAction={endTest}
                          type="filled"
                          color="#7A5AF8"
                        />
                        <Button
                          buttonText="Изменить ответ"
                          onClickAction={setAnswer}
                          type="filled"
                        />
                      </>
                    ) : (
                      <>
                        {isAnswered === true ? (
                          ""
                        ) : (
                          <>
                            <Button
                              buttonText="Ответить"
                              onClickAction={setAnswer}
                              type="filled"
                              color="#7A5AF8"
                            />
                            <Button
                              buttonText="Пропустить →"
                              onClickAction={skipQuestion}
                              type="skip"
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {currentQuestion &&
                    currentQuestion.comment &&
                    currentQuestion.comment !== null && (
                      <div className="commentBox" onClick={handlePopUp}>
                        Комментарий
                        {showPopUp && (
                          <Comment
                            result={currentQuestion}
                            header={getTestTitle(breadCrumbs)}
                            title={"Упражнение"}
                            handlePopUp={handlePopUp}
                          >
                          </Comment>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </DndProvider>
          </div>
        </div>
      </div>
    )
  );
};

export default Exercise;
